// EinteilungPage — Schlanker Shell: lädt Abteilungen und rendert Tabs (Monatsplan / Jahresübersicht / Nächster Dienst).
// Die eigentliche Logik liegt in /components/einteilung/* (MatrixView, JahresansichtView, MagnetboardView).
import React, { useState, useEffect } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { wacheApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import MainLayout from '../components/layout/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import MatrixView from '../components/einteilung/MatrixView';
import MagnetboardView from '../components/einteilung/MagnetboardView';

export function EinteilungPage() {
  const { user, canEditEinteilung, isGlobalRole } = useAuthStore();
  const [selectedAbteilung, setSelectedAbteilung] = useState('');
  const [abteilungenList, setAbteilungenList] = useState([]);

  const isLeitung = isGlobalRole();
  // canEdit ist abteilungs-spezifisch: WAL/ADMIN nur eigene WA, MITARBEITER nur mit Permission
  const effectiveAbteilung = selectedAbteilung || user?.abteilung_id;
  const canEdit = canEditEinteilung(effectiveAbteilung);

  useEffect(() => {
    const loadAbteilungen = async () => {
      try {
        const res = await wacheApi.getAbteilungen();
        setAbteilungenList(res.data);
        if (user?.abteilung_id) {
          setSelectedAbteilung(user.abteilung_id);
        } else if (res.data.length > 0) {
          setSelectedAbteilung(res.data[0].id);
        }
      } catch (error) {
        console.error('Load abteilungen error:', error);
      }
    };
    loadAbteilungen();
  }, [user?.abteilung_id]);

  const selectedAbteilungObj = abteilungenList.find(a => a.id === selectedAbteilung);
  const abteilungName = selectedAbteilungObj?.name || '';

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="schedule-page">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">Diensteinteilung</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Schichtplanung und Funktionszuweisung
            </p>
          </div>

          {isLeitung && abteilungenList.length > 0 && (
            <Select value={selectedAbteilung} onValueChange={setSelectedAbteilung}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="abteilung-select">
                <SelectValue placeholder="Abteilung wählen..." />
              </SelectTrigger>
              <SelectContent>
                {abteilungenList.map(abt => (
                  <SelectItem key={abt.id} value={abt.id}>
                    {abt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Tabs defaultValue="matrix" className="space-y-4">
          <TabsList className="w-full sm:w-auto grid grid-cols-2 sm:inline-flex">
            <TabsTrigger value="matrix" data-testid="tab-matrix" className="text-xs sm:text-sm">
              <Calendar className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Jahresplan</span>
              <span className="sm:hidden ml-1">Plan</span>
            </TabsTrigger>
            <TabsTrigger value="magnetboard" data-testid="tab-magnetboard" className="text-xs sm:text-sm">
              <Clock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nächster Dienst</span>
              <span className="sm:hidden ml-1">Dienst</span>
            </TabsTrigger>
            {/* "Heute" temporär deaktiviert — separater Fix-Task offen */}
          </TabsList>

          <TabsContent value="matrix">
            <MatrixView
              wacheId={user?.wache_id}
              abteilungId={selectedAbteilung || user?.abteilung_id}
              abteilungName={abteilungName}
              canEdit={canEdit}
            />
          </TabsContent>

          <TabsContent value="magnetboard">
            {/* Desktop: zentriert + schmaler (boxed). Mobil: voll Breite. */}
            <div className="sm:max-w-2xl sm:mx-auto">
              <MagnetboardView
                wacheId={user?.wache_id}
                abteilungId={selectedAbteilung || user?.abteilung_id}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}

export default EinteilungPage;
